import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { usePageTitle } from "@/lib/usePageTitle";
import { Clock, KeyRound, ShieldCheck, Sparkles } from "lucide-react";

import { TRADE_CATS } from "@/lib/trades";
const ART = "/brand/trophy.webp";

export default function Partners() {
  const { register } = useAuth();
  const navigate = useNavigate();
  usePageTitle("Partner with us — Olivia Does");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", trade_category: "Plumbing", company: "", years: "", area: "" });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await register({ name: form.name, email: form.email, password: form.password, phone: form.phone, role: "trade", trade_category: form.trade_category });
    setBusy(false);
    if (res.ok) { setSent(true); toast.success("Application received."); }
    else { toast.error(res.error || "Could not submit application"); }
  };

  const benefits = [
    [Clock, "Pre-scoped jobs", "No bidding wars — we scope it, you decide take it or leave it."],
    [KeyRound, "Paid within 48 hours", "Direct deposit, a transparent 10% fee, nothing else deducted."],
    [ShieldCheck, "Premium customers", "Homeowners who've already chosen to trust the Olivia standard."],
    [Sparkles, "We handle the awkward stuff", "Disputes, chasing payment, customer comms — that's on us, not you."],
  ];

  return (
    <div data-testid="partners-page">
      <section className="od-wrap hero" style={{ paddingTop: 60 }}>
        <div>
          <div className="od-eyebrow">Partner network</div>
          <h1 style={{ fontSize: "3.1rem", lineHeight: 1.08, fontWeight: 500, margin: "0 0 22px" }}>Are you a trade we'd<br />be proud of?</h1>
          <p className="lead">When a job calls for a specific trade — plumbing, electrical, roofing and beyond — that's where our partner panel comes in. You're the specialist we bring in because the job needs exactly what you do.</p>
        </div>
        <div className="heropanel"><img className="brandart" src={ART} alt="A trade we'd be proud of" /></div>
      </section>

      <section className="od-section od-wrap">
        <div className="ogrid cols-2">
          {benefits.map(([Icon, t, d]) => (
            <div className="card" key={t}><Icon className="icon" /><h3>{t}</h3><p>{d}</p></div>
          ))}
        </div>
      </section>

      <section className="od-section-alt od-section">
        <div className="od-wrap">
          <h2 className="serif" style={{ fontSize: "1.8rem", fontWeight: 500 }}>What we look for</h2>
          <ul style={{ color: "var(--taupe)", lineHeight: 2, paddingLeft: 20 }}>
            <li>Trading history and valid public liability insurance</li>
            <li>Two verifiable references from recent work</li>
            <li>Consistently high finish quality</li>
            <li>Fair, transparent pricing</li>
            <li>Room on the panel — we cap every trade at 3–5 partners</li>
          </ul>
        </div>
      </section>

      <section className="od-section od-wrap" style={{ maxWidth: 640 }}>
        <h2 className="serif" style={{ fontSize: "1.7rem", fontWeight: 500, marginBottom: 20 }}>Apply to join</h2>
        {sent ? (
          <div className="card" style={{ borderColor: "var(--green)" }} data-testid="partner-sent">
            <h3 style={{ marginTop: 0 }}>Thanks — that's with us.</h3>
            <p>Olivia will be in touch within 2 working days. In the meantime, your partner account is ready.</p>
            <button className="btn primary" style={{ marginTop: 12 }} onClick={() => navigate("/dashboard")}>Go to your dashboard</button>
          </div>
        ) : (
          <form className="card" onSubmit={onSubmit}>
            <div className="ogrid cols-2" style={{ gap: 16 }}>
              <div className="field"><label className="flabel">Full name</label><input className="finput" data-testid="partner-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="field"><label className="flabel">Trade</label>
                <select className="finput" data-testid="partner-trade" value={form.trade_category} onChange={(e) => setForm({ ...form, trade_category: e.target.value })}>
                  {TRADE_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="field"><label className="flabel">Email</label><input className="finput" data-testid="partner-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="field"><label className="flabel">Mobile</label><input className="finput" data-testid="partner-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="field"><label className="flabel">Company name</label><input className="finput" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              <div className="field"><label className="flabel">Years trading</label><input className="finput" value={form.years} onChange={(e) => setForm({ ...form, years: e.target.value })} /></div>
              <div className="field" style={{ gridColumn: "1/3" }}><label className="flabel">Coverage area</label><input className="finput" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="e.g. South West London" /></div>
              <div className="field" style={{ gridColumn: "1/3" }}><label className="flabel">Create a password</label><input className="finput" data-testid="partner-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            </div>
            <button className="btn primary block" data-testid="partner-submit" disabled={busy} type="submit">{busy ? "Submitting…" : "Submit application"}</button>
          </form>
        )}
        <p style={{ fontSize: ".85rem", color: "var(--taupe)", marginTop: 16 }}>Already a partner? <Link to="/login" className="od-link">Sign in</Link></p>
      </section>
    </div>
  );
}
